import type {
    GetProfessionalPagesRootDataQuery,
    GetProjectDetailQuery,
    Article,
    Product,
    SupportedCountry,
} from 'generated/graphql';
import {
    isSupported,
    parseCountry,
    swapDomain,
} from '@archipro-website/localisation';

const getThirdPathSegment = (url?: string): string | null => {
    if (!url) return null;
    const pathSegments = url.split('/').filter(Boolean);
    return pathSegments.length >= 3 ? (pathSegments[2] ?? null) : null;
};

export type AssociatedPagesType =
    | Product['AssociatedPages']
    | Article['AssociatedPages']
    | Extract<
          GetProjectDetailQuery['Directory']['Project'],
          { __typename: 'Project' }
      >['AssociatedPages']
    | Extract<
          GetProfessionalPagesRootDataQuery['Directory']['professionalInitial'],
          { __typename: 'Professional' }
      >['AssociatedPages'];

export interface CloneCountryState {
    urlSegment: string | null;
    currentCountry: string | null;
    pageType: string | null;
    associatedPages: AssociatedPagesType | null;
}

export interface HeaderCountryElement {
    code: string;
    name: string;
    url: string;
}

/** AssociatedPages Country shape after Code was added to the GraphQL fragments. */
type AssociatedCountry = {
    Code?: string | null;
};

const associatedPageMatchesCountry = (
    edge: AssociatedPagesType['edges'][number] | null | undefined,
    countryCode: string,
): boolean => {
    const code = (edge?.node?.Zone?.Country as AssociatedCountry | null)?.Code;
    if (!code || !isSupported(code) || !isSupported(countryCode)) {
        return false;
    }
    return code === countryCode;
};

export const getCountrySwitchUrl = (
    country: HeaderCountryElement,
    countryData: CloneCountryState,
    currentUrl: string,
): string => {
    const pathRoot = currentUrl.split('/').filter(Boolean)[0] ?? '';
    // Clone state can linger after client-nav to a listing (e.g. /professionals).
    // Only use AssociatedPages while the URL is still that detail page type.
    const isOnCloneableDetail =
        Boolean(countryData?.pageType) && pathRoot === countryData.pageType;

    // Not on a cloneable detail page — keep the path on the target market
    // (AppPage already strips product/professional/project/article to homepage).
    if (!isOnCloneableDetail || !countryData?.associatedPages?.edges?.length) {
        return country.url;
    }

    const edges = countryData.associatedPages
        .edges as AssociatedPagesType['edges'][number][];

    // Already on this market — stay on the current page (AssociatedPages are
    // siblings only, so the current country often has no self edge).
    if (country.code === countryData.currentCountry) {
        return currentUrl;
    }

    const associatedEdgePage = edges.find((edge) =>
        associatedPageMatchesCountry(edge, country.code),
    );

    // Twin exists for the target market — use its AbsoluteLink (domain-swapped)
    const baseUrl = associatedEdgePage?.node?.ZonePage?.Link;
    if (baseUrl) {
        const newDomain = handleDomainOrigin(
            baseUrl,
            country.code as SupportedCountry,
        );
        const thirdPathSegment = getThirdPathSegment(currentUrl);
        return thirdPathSegment
            ? `${newDomain}/${thirdPathSegment}`
            : newDomain;
    }

    // No twin for this market — go to that market's homepage
    // (for product/professional/project/article, country.url is already origin-only)
    return country.url;
};

/** Replaces the domain in a URL so it points to the target country. */
const handleDomainOrigin = (
    url: string,
    targetCountryCode: SupportedCountry,
): string => {
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;

    try {
        const urlObj = new URL(fullUrl);
        const currentCountryCode = parseCountry.host(urlObj.hostname);

        if (!currentCountryCode) {
            return url;
        }

        urlObj.hostname = swapDomain(
            urlObj.hostname,
            currentCountryCode,
            targetCountryCode,
        );
        return urlObj.toString();
    } catch {
        return url;
    }
};
